import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserInterface } from 'src/app/models/user.interface';
import { UserService } from 'src/app/core/services/user.service';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { ConfirmEventType, ConfirmationService, MessageService } from 'primeng/api';

@Component({
  selector: 'globot-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit {
  user: UserInterface | undefined = undefined;
  userForm: FormGroup;

  imageChangedEvent: any = '';
  croppedImage: any = '';
  imageUploaded = false;
  isLoading: boolean = false;
  urlProfile: string = '';
  dialogVisible: boolean = false;
  hasPicture: boolean = false;
  isUpdating: boolean = false;

  constructor(
    private readonly userService: UserService,
    private fb: FormBuilder,
    private http: HttpClient,
    private cd: ChangeDetectorRef,
    private readonly ms: MessageService,
    private confirmationService: ConfirmationService
  ) {
    this.userForm = this.fb.group({
      email: [''],
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(32),
          this.validarFormatoNombre,
        ],
      ],
      type_document: ['DNI'],
    });
    this.user = this.userService.getUserLocalStorage();
    this.updateHasPicture();
  }

  ngOnInit(): void {
    if (this.user) {
      this.userForm.controls['email'].setValue(this.user.email || '');
      this.userForm.controls['name'].setValue(this.user.name || '');
    }
    this.userService.url$.subscribe((newUrl) => {
      this.urlProfile = newUrl;
      this.updateHasPicture();
      this.cd.detectChanges();
    });

    this.userService.name$.subscribe((newName) => {
      if (this.user) {
        this.user.name = newName;
      }
      this.cd.detectChanges();
    });
    this.updateHasPicture();
  }

  validarFormatoNombre(control: AbstractControl) {
    const nombre = control.value.trim();
    const regexNombre = /^[A-Za-zÑñÁÉÍÓÚáéíóúÜü0-9.,]+(?:\s[A-Za-zÑñÁÉÍÓÚáéíóúÜü0-9.,]+)*$/;
    if (!regexNombre.test(nombre)) {
      return { formatoInvalido: 'Ingresa un nombre y apellido válido' };
    }
    return null;
  }
  getInputClass(controlName: string) {
    return {
      'invalid-input':
        this.userForm.controls[controlName].invalid && this.userForm.controls[controlName].touched,
    };
  }
  showDialog() {
    this.dialogVisible = true;
  }

  hideDialog() {
    this.dialogVisible = false;
  }

  fileChangeEvent(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement.files && inputElement.files.length > 0) {
      const file = inputElement.files[0];

      const validTypes = ['image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        alert('Por favor, seleccione un archivo JPG o PNG.');
        inputElement.value = '';
        return;
      }

      if (file.size > 1048576) {
        alert('El tamaño de la imagen no debe superar 1MB');

        inputElement.value = '';
        return;
      }

      this.imageChangedEvent = event;
      this.dialogVisible = true;
    }
  }

  imageCropped(event: ImageCroppedEvent): void {
    this.croppedImage = event.base64;
  }

  imageLoaded(): void {
    // show cropper
  }

  cropperReady(): void {
    // cropper ready
  }

  loadImageFailed(): void {
    // show message
  }

  uploadCroppedImage(): void {
    if (this.croppedImage) {
      this.isLoading = true;
      const blob = this.dataURItoBlob(this.croppedImage);
      const formData = new FormData();
      const inputElement = this.imageChangedEvent.target as HTMLInputElement; // Convertimos el evento de cambio de imagen en un elemento de entrada HTML
      const fileName = inputElement.files ? inputElement.files[0].name : 'profile.png'; // Obtenemos el nombre del archivo seleccionado. Si no hay archivos, usamos 'profile.png'
      formData.append('picture', blob, fileName);

      this.userService.uploadImage(formData).subscribe(
        (response) => {
          if (response?.url) {
            this.userService.changeUrl(response?.url);
            this.imageChangedEvent = '';
            this.croppedImage = '';
            this.dialogVisible = false;
            this.updateHasPicture();
            this.cd.detectChanges();
          }
          this.isLoading = false;
        },
        (error) => {
          console.error('Error de carga:', error);
          this.isLoading = false;
        }
      );
    }
  }

  cancelImageUpload(): void {
    this.imageChangedEvent = '';
    this.croppedImage = '';
    this.imageUploaded = false;

    const fileInput = document.getElementById('avatar-upload') as HTMLInputElement | null;
    if (fileInput) {
      fileInput.value = '';
    }
    this.dialogVisible = false;
  }

  dataURItoBlob(dataURI: string): Blob {
    const byteString = window.atob(dataURI.split(',')[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const intArray = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      intArray[i] = byteString.charCodeAt(i);
    }
    return new Blob([intArray], { type: 'image/png' });
  }
  updateHasPicture(): void {
    this.hasPicture = !!this.urlProfile || !!this.user?.picture;
  }

  deletePicture() {
    this.confirmationService.confirm({
      message: 'Si eliminas no podrás recuperar los datos.',
      icon: 'pi pi-exclamation-triangle',
      key: 'deletePictureDialog',
      accept: () => {
        this.userService.deletePictureUser().subscribe({
          next: () => {
            this.showMessage('success', 'Acción exitosa', 'Foto de perfil eliminada con éxito');
            this.urlProfile = '';
            if (this.user) {
              this.user.picture = '';
            }
            this.userService.changeUrl('');

            // Actualizar el estado de hasPicture
            this.updateHasPicture();
            this.cd.detectChanges();
            this.confirmationService.close();
          },
          error: (error) => {
            console.error('Error al eliminar la imagen:', error);
            this.showMessage(
              'error',
              'Error al eliminar la imagen',
              'Por favor, intenta más tarde'
            );
            this.confirmationService.close();
          },
        });
      },
      reject: (type: ConfirmEventType) => {
        switch (type) {
          case ConfirmEventType.REJECT:
          case ConfirmEventType.CANCEL:
            this.showMessage('warn', 'Acción cancelada', 'Eliminación de imagen cancelada');
            break;
        }
        this.confirmationService.close();
      },
    });
  }
  showMessage(severity: string, summary: string, detail: string) {
    this.ms.add({ key: 'tst', severity, summary, detail });
  }
  updateUser() {
    if (!this.userForm.valid) {
      this.showMessage('error', 'No se realizaron cambios', 'Es necesario un nombre');
      return;
    }

    if (!this.userForm.dirty) {
      this.showMessage('info', 'Sin cambios', 'No hay cambios para guardar.');
      return;
    }

    if (this.user) {
      this.isUpdating = true;
      const updatedUser: UserInterface = {
        ...this.user,
        name: this.userForm.get('name')?.value.trim(),
        type_document: this.userForm.get('type_document')?.value,
      };

      this.userService.updateUser(updatedUser).subscribe({
        next: (response) => {
          this.user = response;
          this.userService.changeName(response.name);
          this.userForm.markAsPristine();
          this.cd.detectChanges();
          this.showMessage('success', 'Acción exitosa', '¡Cambios guardados!');
          this.isUpdating = false;
        },
        error: (error) => {
          this.isUpdating = false;
          console.error('Error al actualizar el perfil:', error);
          this.showMessage(
            'error',
            'Error',
            'No se pudo actualizar el perfil. Por favor, intenta de nuevo.'
          );
        },
      });
    } else {
      this.showMessage(
        'warn',
        'Formulario inválido',
        'Por favor, verifica los campos del formulario.'
      );
    }
  }
  selectImage(): string {
    if (this.urlProfile !== undefined && this.urlProfile !== '') {
      return this.urlProfile;
    } else if (this.user?.picture !== undefined && this.user.picture !== '') {
      return this.user.picture;
    } else {
      return '';
    }
  }
}
